'use client';

import { useCallback, useEffect, useState } from 'react';

/**
 * Code-split a tap-triggered component WITHOUT paying for the split at tap
 * time.
 *
 * `next/dynamic` / `React.lazy` fetch the chunk on the first render and then
 * suspend on it, so the first open of a modal costs the chunk's network
 * round-trip plus React's ~300ms Suspense reveal throttle (measured ~370ms
 * tap-to-panel even with the chunk cached). This hook instead loads the
 * module during idle time after hydration and hands back the resolved export
 * as plain state; rendering it later is synchronous. If the shopper taps
 * before idle, call `load()` — the component appears as soon as the chunk
 * lands, which is no worse than today.
 *
 * Resolution is cached per loader, so many instances (e.g. 24 product cards)
 * share one fetch and one module. Pass a module-level loader so its identity
 * is stable: `const loadX = () => import('./x').then((m) => m.X)`.
 */
const cache = new WeakMap<() => Promise<unknown>, { value?: unknown; promise: Promise<unknown> }>();

function resolve<T>(load: () => Promise<T>): Promise<T> {
	const existing = cache.get(load);
	if (existing) return existing.promise as Promise<T>;
	const entry: { value?: unknown; promise: Promise<unknown> } = { promise: Promise.resolve() };
	entry.promise = load().then((value) => {
		entry.value = value;
		return value;
	});
	cache.set(load, entry);
	return entry.promise as Promise<T>;
}

function peek<T>(load: () => Promise<T>): T | undefined {
	return cache.get(load)?.value as T | undefined;
}

export function useIdleModule<T>(
	load: () => Promise<T>,
	{ enabled = true }: { enabled?: boolean } = {},
): { module: T | null; load: () => void } {
	const [module, setModule] = useState<T | null>(() => peek(load) ?? null);

	const trigger = useCallback(() => {
		// Components are functions: always pass an updater so React doesn't call
		// the module itself as one.
		const ready = peek(load);
		if (ready !== undefined) {
			setModule(() => ready);
			return;
		}
		void resolve(load).then((value) => setModule(() => value));
	}, [load]);

	useEffect(() => {
		if (!enabled || peek(load) !== undefined) return;
		if (typeof requestIdleCallback !== 'function') {
			const timer = setTimeout(trigger, 1500);
			return () => clearTimeout(timer);
		}
		const idle = requestIdleCallback(trigger, { timeout: 3000 });
		return () => cancelIdleCallback(idle);
	}, [enabled, load, trigger]);

	return { module, load: trigger };
}
